/**
 * Centralized pricing features configuration
 *
 * Single source of truth for all Free vs Premium feature displays.
 * Used by: Pricing page, PlanSelection page, Onboarding SubscriptionStep
 *
 * Updated 2026-02-01: Meal photo is now the only premium feature
 * All other features moved to free tier
 *
 * To add/remove features:
 * 1. Update the arrays below
 * 2. Add corresponding translation keys to locales/en.json and locales/sv.json
 *    under pricing.features.free.* and pricing.features.premium.*
 */

/**
 * Free plan features - translation keys
 * These are displayed on all pricing/plan selection pages
 * Updated: Now includes most features that were previously premium
 */
export const FREE_FEATURES = [
  'pricing.features.free.iobCalculator',
  'pricing.features.free.dosingRules',
  'pricing.features.free.cgmIntegration',
  'pricing.features.free.glucoseThresholds',
  'pricing.features.free.unlimitedProfiles',       // Previously premium
  'pricing.features.free.fullHistory',             // Previously premium (was recentHistory)
  'pricing.features.free.sensorStatistics',        // Previously premium
  'pricing.features.free.medicalReports',          // Previously premium
  'pricing.features.free.fullFoodDatabase',        // Previously premium
  'pricing.features.free.sensorReclaim',
  'pricing.features.free.healthTracking',
  'pricing.features.free.mealPhotoLimited',        // NEW: 3 AI meal photos/day
];

/**
 * Premium plan features - translation keys
 * First item is highlighted as intro ("Everything in Free, PLUS:")
 * Features with comingSoon: true will have "(coming soon)" appended
 *
 * Updated: Only unlimited meal photos is the real premium feature now
 */
export const PREMIUM_FEATURES = [
  { key: 'pricing.features.premium.everythingInFree', highlight: true },
  'pricing.features.premium.unlimitedMealPhotos',  // THE ONLY REAL PREMIUM FEATURE
  { key: 'pricing.features.premium.aiInsights', comingSoon: true },
];

/**
 * Helper to transform feature arrays for PlanCard component
 * @param {Function} t - i18next translation function
 * @param {Array} features - Feature array (FREE_FEATURES or PREMIUM_FEATURES)
 * @param {string} comingSoonKey - Translation key for "(coming soon)" text
 * @returns {Array} Formatted features for PlanCard
 */
export function getFormattedFeatures(t, features, comingSoonKey = 'pricing.features.comingSoon') {
  return features.map((feature) => {
    if (typeof feature === 'string') {
      return t(feature);
    }

    const { key, highlight, comingSoon } = feature;
    let text = t(key);

    if (comingSoon) {
      text = `${text} ${t(comingSoonKey)}`;
    }

    if (highlight) {
      return { text, highlight: true };
    }

    return text;
  });
}
